import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  AlertCircle,
  AlertTriangle,
  Clock,
  CheckCircle,
  Filter,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIEEN = ['Chemie', 'Koffie', 'Schoonmaak', 'Techniek', 'Energie', 'Water', 'Afval', 'Anders'];

const ContractenOverzicht = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [locaties, setLocaties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    categorie: searchParams.get('categorie') || '',
    locatie: searchParams.get('locatie') || ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.categorie) params.append('categorie', filters.categorie);
      if (filters.locatie) params.append('locatie_id', filters.locatie);
      
      const response = await axios.get(`${API}/management/contracten?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching contracten:', error);
      toast.error('Fout bij laden contracten');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchLocaties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locaties`);
      setLocaties(response.data);
    } catch (error) {
      console.error('Error fetching locaties:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocaties();
  }, [fetchLocaties]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.categorie) params.set('categorie', newFilters.categorie);
    if (newFilters.locatie) params.set('locatie', newFilters.locatie);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ status: '', categorie: '', locatie: '' });
    setSearchParams({});
  };

  const exportCSV = () => {
    if (!data?.contracten) return;
    
    const headers = ['Locatie', 'Leverancier', 'Categorie', 'Einddatum', 'Status', 'Kosten/jaar', 'Auto verlengen'];
    const rows = data.contracten.map(c => [
      c.locatieNaam,
      c.bedrijfsnaam,
      c.categorie,
      c.einddatum,
      c.status,
      c.kostenPerJaar || '-',
      c.automatischVerlengen ? 'Ja' : 'Nee'
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contracten-overzicht-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV geëxporteerd');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verlopen':
        return <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" />Verlopen</Badge>;
      case 'binnenkort':
        return <Badge variant="secondary" className="bg-red-100 text-red-800 gap-1"><AlertTriangle className="w-3 h-3" />&lt; 1 maand</Badge>;
      case 'binnen6m':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 gap-1"><Clock className="w-3 h-3" />&lt; 6 maanden</Badge>;
      default:
        return <Badge variant="secondary" className="bg-green-100 text-green-800 gap-1"><CheckCircle className="w-3 h-3" />OK</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-7 h-7" />
            Contracten Overzicht
          </h1>
          <p className="text-slate-500">Leverancierscontracten alle locaties</p>
        </div>
        <Button onClick={exportCSV} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Exporteer CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-800">{data?.stats?.totaal || 0}</div>
            <div className="text-sm text-slate-500">Totaal</div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{data?.stats?.verlopen || 0}</div>
            <div className="text-sm text-slate-500">Verlopen</div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{data?.stats?.binnenkort || 0}</div>
            <div className="text-sm text-slate-500">&lt; 1 maand</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{data?.stats?.binnen6m || 0}</div>
            <div className="text-sm text-slate-500">&lt; 6 maanden</div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{data?.stats?.ok || 0}</div>
            <div className="text-sm text-slate-500">OK</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="verlopen">Verlopen</SelectItem>
                <SelectItem value="binnenkort">&lt; 1 maand</SelectItem>
                <SelectItem value="binnen6m">&lt; 6 maanden</SelectItem>
                <SelectItem value="ok">OK</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.categorie} onValueChange={(v) => handleFilterChange('categorie', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIEEN.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.locatie} onValueChange={(v) => handleFilterChange('locatie', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Locatie" />
              </SelectTrigger>
              <SelectContent>
                {locaties.map(loc => (
                  <SelectItem key={loc._id} value={loc._id}>{loc.naam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {(filters.status || filters.categorie || filters.locatie) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Wis filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Contracten ({data?.contracten?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 font-medium text-slate-500">Locatie</th>
                  <th className="pb-3 font-medium text-slate-500">Leverancier</th>
                  <th className="pb-3 font-medium text-slate-500">Categorie</th>
                  <th className="pb-3 font-medium text-slate-500">Einddatum</th>
                  <th className="pb-3 font-medium text-slate-500">Kosten/jaar</th>
                  <th className="pb-3 font-medium text-slate-500">Auto verlengen</th>
                  <th className="pb-3 font-medium text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.contracten?.map((contract, idx) => (
                  <tr key={`${contract.locatieId}-${contract.leverancierId}-${idx}`} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-3">
                      <div className="font-medium text-slate-800">{contract.locatieNaam}</div>
                      <div className="text-sm text-slate-500">{contract.locatieType}</div>
                    </td>
                    <td className="py-3 font-medium">{contract.bedrijfsnaam}</td>
                    <td className="py-3 text-slate-600">{contract.categorie}</td>
                    <td className="py-3">
                      <span className={contract.status === 'verlopen' ? 'text-red-600 font-medium' : ''}>
                        {formatDate(contract.einddatum)}
                      </span>
                    </td>
                    <td className="py-3 text-slate-600">{formatCurrency(contract.kostenPerJaar)}</td>
                    <td className="py-3">
                      {contract.automatischVerlengen ? (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800">Ja</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600">Nee</Badge>
                      )}
                    </td>
                    <td className="py-3">{getStatusBadge(contract.status)}</td>
                  </tr>
                ))}
                {(!data?.contracten || data.contracten.length === 0) && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500">
                      Geen contracten gevonden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractenOverzicht;
